import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { projectFilePath } from "./db/filesystem.service";
import SQLDatabase from "./db/sql.service";
import { AppConfig } from "../config";
import * as mapboxgl from 'mapbox-gl/esm';

const getMapIconUrl = async (projectId: number, itemId: number) => {
  const path = projectFilePath(projectId, 'items', itemId);
  const result = await Filesystem.getUri({
    directory: Directory.Data,
    path
  });
  return Capacitor.convertFileSrc(result.uri);
}

export class MapIconService {

  private iconMap: Map<string, string> = new Map();
  private isNative: boolean = Capacitor.isNativePlatform();

  private constructor() { }

  public static instance: MapIconService = new MapIconService();

  private readonly itemQuery = `SELECT id, icon, color FROM items WHERE projectId = ? AND icon IS NOT NULL`;

  /**
   * This function builds the icon map for the project. If a map has been provided, it will also add the icons to the map.
   */
  public async loadIcons(projectId: number, config: AppConfig) {
    this.iconMap.clear();
    this.iconMap.set('ukhab-tree', '/assets/imgs/ukhab-tree.png');
    const items = await SQLDatabase.instance.query<{ id: number; icon: string }>(this.itemQuery, [projectId]);

    const processItem = async (item: { id: number; icon: string; }) => {
      const key = `${projectId}_${item.id}`;
      let url: string;
      if (this.isNative) {
        url = await getMapIconUrl(projectId, item.id);
      } else {
        const iconUrl = new URL(item.icon);
        url = `${config.CDN_URL}${iconUrl.pathname}`;
      }
      this.iconMap.set(key, url);
    };

    return Promise.all(items.map(processItem));
  }

  public async handleIcon(map: mapboxgl.Map, id: string) {
    if (this.iconMap.has(id)) {
      const url = this.iconMap.get(id);
      return this.applyIcon(map, id, url);
    }
    return null;
  }

  private generateFill(color: string) {
    const size = 20;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx!.fillStyle = color;
    ctx!.fillRect(0, 0, size, size);
    return ctx!.getImageData(0, 0, canvas.width, canvas.height);
  }

  public async handleColor(map: mapboxgl.Map, color: string) {
    if (map.hasImage(color)) {
      return;
    }
    const img = this.generateFill(color);
    map.addImage(color, img);
  }

  public async applyIcon(map: mapboxgl.Map, key: string, url: string) {
    return new Promise<void>((resolve, reject) => {
      map.loadImage(url, (error, image) => {
        if (error) {
          reject(error);
          return;
        }
        if (!map.hasImage(key)) {
          map.addImage(key, image);
        } else {
          map.removeImage(key);
          map.addImage(key, image);
        }
        resolve();
      });
    });
  }

  public async applyAllIcons(map: mapboxgl.Map) {
    for (const [key, url] of this.iconMap.entries()) {
      await this.applyIcon(map, key, url);
    }
  }
}
